import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { ChevronLeft, ChevronRight, Quote, GraduationCap, Star, PenLine, ExternalLink } from "lucide-react";
import { TESTIMONIALS, COACHES_PARENTS, SITE } from "../lib/site";
import ReviewModal from "./ReviewModal";

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const total = TESTIMONIALS.length;
  const featuredRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 9000);
    return () => clearInterval(t);
  }, [total]);

  const jumpTo = (i) => {
    setIdx(i);
    if (featuredRef.current) {
      featuredRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const current = TESTIMONIALS[idx];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-20 md:py-32">
      <Toaster position="top-center" richColors />
      <ReviewModal open={reviewOpen} onClose={() => setReviewOpen(false)} />
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={featuredRef}>
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 items-end mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-9">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-primary" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">From The Family</span>
            </div>
            <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
              Real players. <br />Real <span className="text-primary">results.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-3 md:text-right">
            <div className="flex md:justify-end gap-2">
              <button
                data-testid="testimonial-prev"
                aria-label="Previous testimonial"
                onClick={() => setIdx((i) => (i - 1 + total) % total)}
                className="p-3 border border-foreground/30 hover:bg-foreground hover:text-background transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                data-testid="testimonial-next"
                aria-label="Next testimonial"
                onClick={() => setIdx((i) => (i + 1) % total)}
                className="p-3 border border-foreground/30 hover:bg-foreground hover:text-background transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Featured card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-12 gap-6 md:gap-10 items-center"
            data-testid={`testimonial-card-${current.id}`}
          >
            <div className="col-span-12 md:col-span-5">
              <div className="relative aspect-[4/5] overflow-hidden bg-secondary">
                <img src={current.image} alt={current.name} className="w-full h-full object-cover object-top" />
                <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em]">
                  {current.accolade}
                </div>
                <div className="absolute bottom-4 right-4 bg-background/95 backdrop-blur-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-foreground">
                  #BeGreat
                </div>
              </div>
            </div>
            <div className="col-span-12 md:col-span-7">
              <Quote className="text-primary/30 mb-4" size={56} strokeWidth={1} />
              <blockquote className="font-display uppercase text-2xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                "{current.quote}"
              </blockquote>
              <div className="mt-8 flex items-center gap-4">
                <div className="h-px w-12 bg-primary" />
                <div>
                  <div className="font-display text-3xl uppercase leading-none">{current.name}</div>
                  <div className="mt-2 text-xs font-bold uppercase tracking-[0.25em] text-primary flex items-center gap-2">
                    <GraduationCap size={14} /> {current.school}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Pagination dots */}
        <div className="mt-10 flex gap-2">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              data-testid={`testimonial-dot-${i}`}
              aria-label={`Go to testimonial ${i + 1}`}
              onClick={() => setIdx(i)}
              className={`h-1 transition-all ${i === idx ? "w-12 bg-primary" : "w-6 bg-foreground/20"}`}
            />
          ))}
        </div>

        {/* All athletes grid (thumbnails to click between) */}
        <div className="mt-20 pt-12 border-t border-foreground/15">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-primary" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">The Roster · {TESTIMONIALS.length} Athletes</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {TESTIMONIALS.map((t, i) => (
              <button
                key={t.id}
                onClick={() => jumpTo(i)}
                data-testid={`testimonial-thumb-${t.id}`}
                className={`group text-left transition-all ${i === idx ? "opacity-100" : "opacity-60 hover:opacity-100"}`}
              >
                <div className={`relative aspect-[4/5] overflow-hidden bg-secondary mb-3 ${i === idx ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`}>
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="font-display text-xl uppercase leading-none">{t.name}</div>
                <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.accolade}</div>
                <div className="mt-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{t.school}</div>
              </button>
            ))}
          </div>
        </div>

        {/* SHARE YOUR STORY CTA */}
        <div className="mt-24 pt-12 border-t border-foreground/15" data-testid="share-story-cta">
          <div className="bg-foreground text-background p-8 md:p-14 grid grid-cols-12 gap-6 items-center grain">
            <div className="col-span-12 md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">Share Your Story</span>
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
                Train with us? <br /><span className="text-primary">Tell the family.</span>
              </h2>
              <p className="mt-5 max-w-lg text-background/75 text-base md:text-lg">
                Help other families discover BeGreat. Drop a quick Google review for visibility, or send us a written testimonial to feature on the site.
              </p>
            </div>
            <div className="col-span-12 md:col-span-5 flex flex-col gap-3 md:items-end">
              <a
                href={SITE.googleReviewUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="google-review-cta"
                className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] hover:bg-background hover:text-foreground transition-colors w-full md:w-auto justify-center"
              >
                <Star size={16} fill="currentColor" /> Leave a Google Review
                <ExternalLink size={12} className="opacity-60" />
              </a>
              <button
                type="button"
                onClick={() => setReviewOpen(true)}
                data-testid="write-review-cta"
                className="group inline-flex items-center gap-3 border-2 border-background text-background px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] hover:bg-background hover:text-foreground transition-colors w-full md:w-auto justify-center"
              >
                <PenLine size={16} /> Write a Review On-Site
              </button>
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-background/50 mt-1">
                ★ Google · We feature select on-site reviews
              </div>
            </div>
          </div>
        </div>

        {/* COACHES & PARENTS */}
        <div className="mt-24 pt-12 border-t border-foreground/15" data-testid="coaches-parents-section">
          <div className="grid grid-cols-12 gap-6 items-end mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-primary" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">From The Coaches & Parents</span>
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
                Built on <span className="text-primary">trust.</span>
              </h2>
            </div>
          </div>

          <div className="columns-1 md:columns-2 lg:columns-3 gap-5 md:gap-6 [column-fill:_balance]">
            {COACHES_PARENTS.map((c) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.5 }}
                data-testid={`coach-parent-${c.id}`}
                className="break-inside-avoid mb-5 md:mb-6 p-6 md:p-7 bg-secondary/40 border border-foreground/10 hover:border-primary transition-colors"
              >
                <Quote size={28} strokeWidth={1.5} className="text-primary mb-3" />
                <p className="text-foreground/85 text-sm md:text-base leading-relaxed">{c.quote}</p>
                <div className="mt-5 pt-4 border-t border-foreground/15">
                  <div className="font-display text-xl uppercase leading-none">{c.name}</div>
                  <div className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{c.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
